import {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import type { User } from '@peles/shared';
import * as authApi from '../api/auth';
import {
  SessionChangedError,
  clearRenewals,
  refreshAccessToken,
  renewImpersonationToken,
  sessionRefused,
} from '../api/client';
import { tokenRole, tokenSubject } from '../utils/accessToken';

interface AuthState {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  realUser: User | null;
  isImpersonating: boolean;
}

type AuthAction =
  | { type: 'SET_LOADING' }
  | { type: 'SET_USER'; user: User }
  | { type: 'CLEAR_USER' }
  | { type: 'START_IMPERSONATION'; impersonatedUser: User; realUser: User }
  | { type: 'STOP_IMPERSONATION'; realUser: User };

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: true };
    case 'SET_USER':
      return { ...state, user: action.user, isLoading: false, isAuthenticated: true };
    case 'CLEAR_USER':
      return {
        user: null,
        isLoading: false,
        isAuthenticated: false,
        realUser: null,
        isImpersonating: false,
      };
    case 'START_IMPERSONATION':
      return {
        ...state,
        user: action.impersonatedUser,
        realUser: action.realUser,
        isImpersonating: true,
        isLoading: false,
        isAuthenticated: true,
      };
    case 'STOP_IMPERSONATION':
      return {
        ...state,
        user: action.realUser,
        realUser: null,
        isImpersonating: false,
        isLoading: false,
        isAuthenticated: true,
      };
  }
}

interface AuthContextValue extends AuthState {
  loginWithGoogle: () => Promise<void>;
  handleGoogleCallback: (code: string) => Promise<void>;
  logout: () => Promise<void>;
  impersonate: (userId: string) => Promise<void>;
  stopImpersonation: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Waits before asking again who is signed in, when the server could not answer. */
export const GET_ME_RETRY_DELAYS_MS = [1000, 3000];

/**
 * Asks who is signed in, trying again while the server cannot answer (offline,
 * a slow start, a 5xx): only a refusal of the session ends it, never a blip.
 */
async function loadMe(): Promise<User> {
  for (let attempt = 0; ; attempt++) {
    try {
      return await authApi.getMe();
    } catch (err) {
      if (sessionRefused(err) || attempt >= GET_ME_RETRY_DELAYS_MS.length) throw err;
      await new Promise((resolve) => setTimeout(resolve, GET_ME_RETRY_DELAYS_MS[attempt]));
    }
  }
}

/**
 * Brings the stored token up to the role the person has now.
 *
 * The screen is drawn from `/auth/me` — who they are in the database — but
 * every request carries the role the token was signed with. When an
 * administrator changes it (a client's account made a vendor's by turning on
 * their vendor portal), the two disagree until the token is renewed: up to
 * fifteen minutes for a session, and for the whole hour of an impersonation,
 * whose token is never refreshed. The page then shows the vendor portal while
 * the server judges every request as a client's — the shared folder refused
 * with a 403. So a disagreement renews the token at once. A renewal that
 * fails leaves the old token, which still opens whatever it opened.
 *
 * Answers false when the stored session is no longer the person fetched, or
 * changed while it was renewed (another tab signed out, signed in, stopped
 * impersonating or impersonated somebody else): the person fetched
 * belongs to a session that is no longer the stored one, and must not be shown.
 */
async function renewIfRoleChanged(user: User, impersonating: boolean): Promise<boolean> {
  const token = localStorage.getItem('access_token');
  // Another window may have switched who is stored while the person was being
  // fetched (an administrator impersonating somebody else): a renewal now would
  // put a token for the person fetched in place of theirs.
  const signedFor = token ? tokenSubject(token) : null;
  if (signedFor !== null && signedFor !== user.id) return false;
  const signedAs = token ? tokenRole(token) : null;
  if (signedAs === null || signedAs === user.systemRole) return true;
  try {
    if (impersonating) await renewImpersonationToken(user.id);
    else await refreshAccessToken();
  } catch (err) {
    if (err instanceof SessionChangedError) return false;
    // Anything else: kept as it was, see above.
  }
  return true;
}

/** How often the load starts again on the stored session before reloading the page. */
const SESSION_RELOADS = 3;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(authReducer, {
    user: null,
    isLoading: true,
    isAuthenticated: false,
    realUser: null,
    isImpersonating: false,
  });

  // Load user on mount if tokens exist
  useEffect(() => {
    // Started again from whatever session is stored whenever the one it read
    // changed under it, so the page never shows one account over another's tokens.
    function load(attempt: number) {
      const token = localStorage.getItem('access_token');
      if (!token) {
        dispatch({ type: 'CLEAR_USER' });
        return;
      }

      const realToken = localStorage.getItem('real_access_token');
      const again = () => {
        if (attempt < SESSION_RELOADS) load(attempt + 1);
        else window.location.reload();
      };

      loadMe()
        .then(async (user) => {
          // A retry waits, and another window may have started or stopped
          // impersonating meanwhile: the person fetched then belongs to a
          // session other than the one read above, so start again from it.
          if (localStorage.getItem('real_access_token') !== realToken) return again();
          if (realToken) {
            // Restore impersonation state
            const realUser = await authApi.getRealUser();
            if (localStorage.getItem('real_access_token') !== realToken) return again();
            if (realUser) {
              if (!(await renewIfRoleChanged(user, true))) return again();
              dispatch({ type: 'START_IMPERSONATION', impersonatedUser: user, realUser });
            } else {
              // Real token is invalid — clean up and treat as normal session
              localStorage.removeItem('real_access_token');
              localStorage.removeItem('real_refresh_token');
              dispatch({ type: 'SET_USER', user });
            }
          } else {
            if (!(await renewIfRoleChanged(user, false))) return again();
            dispatch({ type: 'SET_USER', user });
          }
        })
        .catch((err) => {
          // The server could not be reached: keep the sign-in for the next load
          // rather than throwing away a session that may well still be good.
          if (!sessionRefused(err)) {
            dispatch({ type: 'CLEAR_USER' });
            return;
          }
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('real_access_token');
          localStorage.removeItem('real_refresh_token');
          dispatch({ type: 'CLEAR_USER' });
        });
    }
    load(0);
  }, []);

  const loginWithGoogle = useCallback(async () => {
    const url = await authApi.getGoogleAuthUrl();
    window.location.href = url;
  }, []);

  const handleGoogleCallback = useCallback(async (code: string) => {
    dispatch({ type: 'SET_LOADING' });
    const result = await authApi.googleCallback(code);
    clearRenewals();
    localStorage.setItem('access_token', result.accessToken);
    localStorage.setItem('refresh_token', result.refreshToken);
    dispatch({ type: 'SET_USER', user: result.user });
  }, []);

  const logout = useCallback(async () => {
    const refreshToken = localStorage.getItem('refresh_token');
    if (refreshToken) {
      try {
        await authApi.logout(refreshToken);
      } catch {
        // Ignore logout API errors — clear tokens regardless
      }
    }
    clearRenewals();
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('real_access_token');
    localStorage.removeItem('real_refresh_token');
    dispatch({ type: 'CLEAR_USER' });
  }, []);

  const impersonate = useCallback(
    async (userId: string) => {
      const result = await authApi.impersonateUser(userId);

      // Save current tokens as real tokens
      const currentAccessToken = localStorage.getItem('access_token');
      const currentRefreshToken = localStorage.getItem('refresh_token');
      if (currentAccessToken) localStorage.setItem('real_access_token', currentAccessToken);
      if (currentRefreshToken) localStorage.setItem('real_refresh_token', currentRefreshToken);

      // Store impersonated session token
      clearRenewals();
      localStorage.setItem('access_token', result.accessToken);

      const realUser = state.user!;
      dispatch({ type: 'START_IMPERSONATION', impersonatedUser: result.user, realUser });
    },
    [state.user],
  );

  const stopImpersonation = useCallback(() => {
    const realAccessToken = localStorage.getItem('real_access_token');
    const realRefreshToken = localStorage.getItem('real_refresh_token');

    clearRenewals();
    if (realAccessToken) localStorage.setItem('access_token', realAccessToken);
    if (realRefreshToken) localStorage.setItem('refresh_token', realRefreshToken);

    localStorage.removeItem('real_access_token');
    localStorage.removeItem('real_refresh_token');

    const realUser = state.realUser!;
    dispatch({ type: 'STOP_IMPERSONATION', realUser });
  }, [state.realUser]);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        loginWithGoogle,
        handleGoogleCallback,
        logout,
        impersonate,
        stopImpersonation,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
